"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { eventsApi } from "@/lib/api/services/events.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Plus, Clock, MapPin } from "lucide-react";
import Link from "next/link";
import { useRequireAuth } from "@/hooks/use-auth-gate";

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  location: string;
  type: string;
  status: string;
  organizer: string;
  createdAt: string;
}

export default function EventsPage() {
  const { data: events, isLoading } = useQuery({
    queryKey: ["events-list"],
    queryFn: async () => {
      const data = await eventsApi.list();
      return ((data as unknown as { data?: Event[] }).data ?? []) as Event[];
    },
  });

  const { requireAuth, AuthModal } = useRequireAuth("/events/create");

  const handleCreateEvent = (e: React.MouseEvent) => {
    if (!requireAuth()) {
      e.preventDefault();
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Events</h1>
          <p className="mt-1 text-sm text-muted-foreground">Discover and manage events.</p>
        </div>
        <Button asChild size="sm">
          <Link href="/events/create" onClick={handleCreateEvent}>
            <Plus className="mr-2 h-4 w-4" /> Create Event
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted/10 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : !events || events.length === 0 ? (
            <div className="py-16 text-center">
              <Calendar className="mx-auto h-10 w-10 text-muted-foreground/30" />
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground">No events</h3>
              <p className="mt-1 text-sm text-muted-foreground">Check back later for upcoming events.</p>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {events.map((event) => (
                <Link key={event.id} href={`/events/${event.id}`} className="flex items-center gap-3 p-4 transition-colors hover:bg-muted/10">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted/10 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{event.title}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{event.description}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                    <span className="hidden sm:flex items-center gap-1"><Clock className="h-3 w-3" /> {event.date}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {event.location}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    <AuthModal />
    </div>
  );
}



